import { useState, useEffect, useCallback, useRef } from 'react';
import { Modal } from 'react-bootstrap';
import { toast, ToastContainer } from 'react-toastify';
import { ErrorMessage } from '@hookform/error-message';
import { useForm, Controller } from 'react-hook-form';
import FormInput from '../FoodMenu/FormInput';
import {
    getAllTable,
    getAllCategoryList,
    updateTableDetails,
    addNewTable,
} from '../../../services/SettingsService';
import { objectToFormData } from '../../../utils/objectToFormData';
import uploadImageIcon from '../../../images/upload_image.png';
import FormSelectInput from './FormSelectInput';
import 'react-toastify/dist/ReactToastify.css';

// Toast update function: uses toast.update() and unique toast IDs
const notify = (id, type, message, time) => {
    toast.update(id, {
        render: message,
        type: type,
        position: 'top-center',
        autoClose: time,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        isLoading: false,
    });
};

const AddTable = (props) => {
    const {
        addNewModal,
        setAddNewModal,
        tableDetails,
        tableId,
    } = props;

    const [tableList, setTableList] = useState();
    const [categoryList, setCategoryList] = useState();
    const uploadRef = useRef(null);

    const {
        handleSubmit,
        formState: { errors },
        register,
        setValue,
        control,
        watch,
    } = useForm({ mode: 'all' });

    const { ref } = register('img');
    const watchImage = watch('img', null);

    // Fetch table list
    const getTableList = useCallback(() => {
        getAllTable().then(res => {
            res.forEach(each => {
                each.label = each.name;
                each.value = each.id;
            });
            let mappedTableList = res
                .map(each => {
                    each.value = +each.value;
                    return each;
                })
                .filter(each => each.status);
            setTableList(mappedTableList);
        });
    }, [setTableList]);

    // Fetch categories
    const getCategoryList = useCallback(() => {
        getAllCategoryList().then(res => {
            res.forEach(each => {
                each.label = each.name;
                each.value = each.id;
            });
            let mappedCategoryList = res
                .map(each => {
                    each.value = +each.value;
                    return each;
                })
                .filter(each => each.status);
            setCategoryList(mappedCategoryList);
        });
    }, [setCategoryList]);

    useEffect(() => {
        getCategoryList();
    }, [getCategoryList]);

    useEffect(() => {
        if (tableId && tableDetails && categoryList) {
            const fields = [
                'table_no',
                'nop',
                'min_nop',
                'desc',
                'category',
                'status',
            ];
            fields.forEach(field => {
                if (field === 'category') {
                    let categoryValue = categoryList.find(
                        category => category.value === tableDetails.category ||
                            category.label === tableDetails.category
                    );
                    if (categoryValue) {
                        setValue(field, categoryValue);
                    }
                } else {
                    setValue(field, tableDetails[field]);
                }
            });
        }
    }, [setValue, tableId, tableDetails, categoryList]);

    // Main form submission logic
    const onSubmit = async data => {
        let toaster;
        if (tableId && tableDetails) {
            // Update existing table logic
            const fields = [
                'table_no',
                'nop',
                'min_nop',
                'img',
                'desc',
                'category',
                'status',
            ];
            fields.forEach(field => {
                if (field === 'img' && (!data[field] || data[field].length < 1)) {
                    delete data[field];
                } else if (field === 'img') {
                    data.img = data.img[0];
                } else if (field === 'category' && typeof data[field] === 'object') {
                    data.category = data.category.value;
                }
                if (data[field] === undefined) {
                    delete data[field];
                }
            });
            let postData = objectToFormData(data, '');
            toaster = toast.loading('Updating...'); // Only one initial toast per action
            let res = await updateTableDetails(tableId, postData);
            console.log(res, 'res');
            if (res.status === 'success') {
                notify(toaster, 'success', 'Table details updated!', 2000); // Update the same toast
                setAddNewModal(false);
            } else {
                notify(toaster, 'error', res.data.message, 2000);
            }
        } else {
            // Create new table logic
            toaster = toast.loading('Creating...');
            data.img = data.img[0];
            let postData = objectToFormData(data);
            let res = await addNewTable(postData);
            if (res.status === 'success') {
                notify(toaster, 'success', 'Created new Table!', 2000);
                setAddNewModal(false);
            } else {
                notify(toaster, 'error', res.data.message, 2000);
            }
        }
    };

    // Loading state for table fetch
    if (tableId && !tableDetails) {
        return (
            <div id='preloader-inner'>
                <div className='sk-three-bounce-inner'>
                    <div className='sk-child sk-bounce1'></div>
                    <div className='sk-child sk-bounce2'></div>
                    <div className='sk-child sk-bounce3'></div>
                    <div id='preloader-inner-bg'></div>
                </div>
            </div>
        );
    }

    // Render modal with form
    return (
        <>
            <Modal
                show={addNewModal}
                onHide={() => setAddNewModal(false)}
                className='modal fade'
                id='addNewModal'
            >
                <div className='modal-content'>
                    <div className='modal-header'>
                        <h5 className='modal-title'>
                            {tableDetails && tableId ? 'Update Table' : 'Add Table'}
                        </h5>
                        <button
                            type='button'
                            className='close'
                            data-dismiss='modal'
                            onClick={() => setAddNewModal(false)}
                        >
                            <span>×</span>
                        </button>
                    </div>
                    <div className='modal-body'>
                        <form onSubmit={handleSubmit(onSubmit)}>
                            <div className='image-placeholder'>
                                <div
                                    className='avatar-edit'
                                    onClick={() => uploadRef?.current?.click()}
                                >
                                    <input
                                        type='file'
                                        {...register('img', {
                                            required: tableId && tableDetails
                                                ? false
                                                : 'image is required!',
                                        })}
                                        name='img'
                                        ref={e => {
                                            ref(e);
                                            uploadRef.current = e;
                                        }}
                                        className='custom-file-input'
                                        id='val-img'
                                        hidden
                                    />
                                    <label htmlFor='imageUpload' name=''></label>
                                </div>
                                <div className='avatar-preview'>
                                    <div id='imagePreview'>
                                        {(tableDetails ||
                                            uploadRef?.current?.files?.length) ? (
                                                <img
                                                    id='saveImageFile'
                                                    src={
                                                        watchImage &&
                                                            watchImage.length &&
                                                            uploadRef.current.files?.length
                                                            ? URL.createObjectURL(
                                                                uploadRef.current?.files[0]
                                                            )
                                                            : process.env.REACT_APP_API_RESOURCE_URL +
                                                            tableDetails?.img
                                                    }
                                                    alt='upload table pic here'
                                                />
                                            ) : (
                                                <img
                                                    className='mr-3 img-fluid rounded'
                                                    src={uploadImageIcon}
                                                    alt='upload table pic here'
                                                />
                                            )}
                                    </div>
                                    <ErrorMessage
                                        errors={errors}
                                        name='img'
                                        render={({ message }) => (
                                            <div style={{ fontSize: "10px" }} className='text-danger mt-2'>
                                                <span>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;{message}</span>
                                            </div>
                                        )}
                                    />
                                </div>
                            </div>
                            <FormInput
                                label={'Table_number'}
                                id={'val-table_no'}
                                name={'table_no'}
                                required={true}
                                ErrorMessage={ErrorMessage}
                                register={{
                                    ...register('table_no', {
                                        required: 'Table number is required!',
                                    }),
                                }}
                                errors={errors}
                            />
                            <FormInput
                                label={'Description'}
                                id={'val-desc'}
                                name={'desc'}
                                ErrorMessage={ErrorMessage}
                                register={{
                                    ...register('desc', {
                                        required: 'Description is required!',
                                    }),
                                }}
                                errors={errors}
                            />
                            <div className='d-flex row'>
                                <div className='col-6'>
                                    <FormInput
                                        type='number'
                                        label={'Number of chairs'}
                                        id={'val-nop'}
                                        name={'nop'}
                                        required={true}
                                        ErrorMessage={ErrorMessage}
                                        register={{
                                            ...register('nop', {
                                                required: 'Number of chairs is required!',
                                            }),
                                        }}
                                        errors={errors}
                                    />
                                </div>
                                <div className='col-6'>
                                    <FormInput
                                        type='number'
                                        label={'Min. chairs to book'}
                                        id={'val-min_nop'}
                                        name={'min_nop'}
                                        required={true}
                                        ErrorMessage={ErrorMessage}
                                        register={{
                                            ...register('min_nop', {
                                                required: 'Min. no. of chairs is required!',
                                                validate: (value) => {
                                                    const nop = watch('nop');
                                                    return parseInt(value) <= parseInt(nop) ||
                                                        'Minimum seating capacity cannot be greater than number of chairs';
                                                }
                                            }),
                                        }}
                                        errors={errors}
                                    />
                                </div>
                            </div>
                            <FormSelectInput
                                label={'Category'}
                                id={'val-category'}
                                name={'category'}
                                ErrorMessage={ErrorMessage}
                                Controller={Controller}
                                control={control}
                                errors={errors}
                                required={true}
                                register={{
                                    ...register('category', {
                                        required: 'Category is required!',
                                    }),
                                }}
                                options={
                                    categoryList
                                        ? categoryList
                                        : [{ value: null, label: 'Loading...' }]
                                }
                            />
                            <div className='custom-control custom-checkbox mb-3'>
                                <input
                                    type='checkbox'
                                    defaultChecked
                                    className='custom-control-input'
                                    {...register('status')}
                                    id='val-status'
                                />
                                <label
                                    className='custom-control-label'
                                    htmlFor='val-status'
                                >
                                    Active
                                </label>
                            </div>
                            <button className='btn btn-primary btn-block'>
                                {tableId && tableDetails ? 'Update' : 'Add'}
                            </button>
                        </form>
                    </div>
                </div>
            </Modal>
            {/* Toast container must only occur once globally, but included here for completeness */}
            <ToastContainer position="top-center" newestOnTop />
        </>
    );
};

export default AddTable;
